/** @file createViewportScene: engine, scene, camera, light, grid, axes, marks, quads and gizmo. */
import type { Engine } from '@babylonjs/core/Engines/engine.js';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight.js';
import { Color4 } from '@babylonjs/core/Maths/math.color.js';
import { Vector3 } from '@babylonjs/core/Maths/math.vector.js';
import type { LinesMesh } from '@babylonjs/core/Meshes/linesMesh.js';
import { Scene } from '@babylonjs/core/scene.js';
import type { Profile } from '@zilo/pose3d-format/types/profile';
import { orbitCamera, type OrbitCamera, type OrbitInit } from '@zilo/pose3d-runtime-babylon/camera/orbitCamera';
import { axisLines } from './axisLines';
import { drawableOutline, type DrawableOutline } from './drawableOutline';
import { gizmoView, type GizmoView } from './gizmoView';
import { groundGrid } from './groundGrid';
import { groundMarks, type GroundMarks } from './groundMarks';
import { jointMarks, type JointMarks } from './jointMarks';
import { puppetLayer, type PuppetLayer } from './puppetLayer';
import { quadHandles, type QuadHandles } from './quadHandles';
import { quadOverlay, type QuadOverlay } from './quadOverlay';
import { VIEWPORT_COLORS } from './viewportColors';
import { viewportEngine } from './viewportEngine';
import { VIEWPORT_GROUPS } from './viewportGroups';
import { VIEWPORT_LAYERS } from './viewportLayers';
import type { ViewportLayout } from './viewportLayout';

/**
 * Everything a viewport draws besides the puppet; `setLayout` (on a real size change) sizes the puppet target and the
 * overlay camera; `dispose` releases the scene and the engine once.
 */
export type ViewportScene = {
  engine: Engine; scene: Scene; camera: OrbitCamera; layer: PuppetLayer; grid: LinesMesh; axes: LinesMesh; marks: JointMarks; gizmo: GizmoView;
  ground: GroundMarks; outline: DrawableOutline; quads: QuadOverlay; handles: QuadHandles;
  setLayout(layout: ViewportLayout): void; setGridVisible(on: boolean): void; dispose(): void;
};

/** An injected engine (tests pass a NullEngine) and the camera's first yaw / pitch / viewport. */
export type ViewportSceneOptions = { engine?: Engine; camera?: OrbitInit };

/**
 * A full-resolution MSAA engine (`viewportEngine`) unless one is given, a scene with the viewport background, the
 * runtime's `orbitCamera` rendering the puppet into a pixel-art target and an overlay camera that shows it with the
 * grid, axes, ground marks (`groundMarks`), joint marks, the part outline and quads (`quadOverlay`, `quadHandles`) and gizmo at full resolution (`puppetLayer`; one
 * `scene.render()` draws both), a hemispheric light (for glTF joint meshes). Overlay groups 1..3 (`VIEWPORT_GROUPS`)
 * clear depth first.
 */
export function createViewportScene(canvas: HTMLCanvasElement, profile: Profile, options: ViewportSceneOptions = {}): ViewportScene {
  const engine = options.engine ?? viewportEngine(canvas);
  const scene = new Scene(engine);
  scene.clearColor = Color4.FromHexString(`${VIEWPORT_COLORS.background}ff`);
  const camera = orbitCamera(scene, profile, options.camera);
  scene.activeCamera = camera.camera;
  new HemisphericLight('viewport-sky', new Vector3(0.3, 1, -0.5), scene).intensity = 1.1;
  for (const group of Object.values(VIEWPORT_GROUPS)) scene.setRenderingAutoClearDepthStencil(group, true, true, false);
  const layer = puppetLayer(scene, camera.camera);
  const grid = groundGrid(scene);
  const axes = axisLines(scene);
  for (const m of [grid, axes]) m.layerMask = VIEWPORT_LAYERS.overlay;
  const ground = groundMarks(scene);
  const marks = jointMarks(scene);
  const outline = drawableOutline(scene);
  const quads = quadOverlay(scene);
  const handles = quadHandles(scene);
  const gizmo = gizmoView(scene);
  let disposed = false;
  return {
    engine, scene, camera, layer, grid, axes, marks, gizmo, ground, outline, quads, handles,
    setLayout: (layout) => { layer.setLayout(layout, camera.view()); },
    setGridVisible: (on) => { grid.setEnabled(on); axes.setEnabled(on); },
    dispose: () => {
      if (disposed) return;
      disposed = true;
      engine.stopRenderLoop();
      gizmo.dispose();
      scene.dispose();
      engine.dispose();
    },
  };
}
